/** Join class names, skipping falsy values. */
export function cx(...parts: Array<string | false | null | undefined | 0>): string {
  let out = "";
  for (const p of parts) {
    if (p) out = out ? `${out} ${p}` : p;
  }
  return out;
}

/** Semantic color tones shared by Badge, Callout, Toast, ProgressBar… */
export type Tone = "neutral" | "accent" | "success" | "warn" | "danger" | "info";

/** "Jon Vaughn" → "JV", "jon@www.linkedin.com" → "J", "" → "?" */
export function initials(name: string | null | undefined): string {
  const clean = (name ?? "").replace(/@.*$/, "").replace(/[^\p{L}\p{N}\s._-]/gu, "").trim();
  if (!clean) return "?";
  const words = clean.split(/[\s._-]+/).filter(Boolean);
  const first = words[0]?.[0] ?? "";
  const last = words.length > 1 ? (words[words.length - 1]?.[0] ?? "") : "";
  return (first + last).toUpperCase() || "?";
}

/** Compact number for KPIs: 1234 → "1.2K", 1_250_000 → "1.3M". */
export function compactNumber(n: number | null | undefined): string {
  if (n === null || n === undefined || Number.isNaN(n)) return "—";
  return new Intl.NumberFormat("en-US", { notation: "compact", maximumFractionDigits: 1 }).format(n);
}

/** Pull a human message out of anything thrown. */
export function errorMessage(err: unknown, fallback = "Something went wrong."): string {
  if (!err) return fallback;
  if (typeof err === "string") return err;
  if (typeof err === "object" && "message" in err && typeof (err as { message: unknown }).message === "string") {
    const m = (err as { message: string }).message;
    return m || fallback;
  }
  return fallback;
}

export const FOCUSABLE_SELECTOR = [
  "a[href]",
  "button:not([disabled])",
  'input:not([disabled]):not([type="hidden"])',
  "select:not([disabled])",
  "textarea:not([disabled])",
  '[tabindex]:not([tabindex="-1"])',
  '[contenteditable="true"]',
].join(",");

export function focusableIn(root: HTMLElement): HTMLElement[] {
  return Array.from(root.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)).filter(
    (el) => !el.hasAttribute("disabled") && el.getAttribute("aria-hidden") !== "true" && el.offsetParent !== null,
  );
}

/** The element that currently has focus, looking through shadow roots. */
export function activeElement(root: Document | ShadowRoot = document): Element | null {
  let el = root.activeElement;
  while (el && el.shadowRoot && el.shadowRoot.activeElement) el = el.shadowRoot.activeElement;
  return el;
}
